import { css } from 'styled-components'
import { black, border2, orange, textMuted, white } from './colors'
import { fontBody, fontDisplay } from './fonts'
import { motion, radii, shadows } from './tokens'
import { fontSizes } from './typography'

/**
 * Shared control recipes — the CSS the Button and Field primitives are built
 * from, exported as mixins so a page that is not on a primitive yet still sits
 * on the same treatment. Everything here is tokens; a literal belongs in
 * `colors.ts` or `tokens.ts` first.
 */

/** A flat control: strips the UA button chrome and restores a visible keyboard focus ring. */
export const flatButton = css`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	font-family: inherit;
	color: inherit;

	&:focus-visible {
		outline: 2px solid ${orange};
		outline-offset: 2px;
	}

	&:disabled {
		cursor: default;
	}
`

/** The card-level action button: Lato 700 on the control radius, color shift on hover, press on active. */
export const actionButton = css`
	${flatButton}
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 10px 20px;
	border-radius: ${radii.inner};
	font-family: ${fontDisplay};
	font-weight: 700;
	font-size: ${fontSizes.small};
	transition:
		background-color ${motion.fast},
		transform ${motion.fast};

	&:active:not(:disabled) {
		transform: scale(0.97);
	}
`

/** The standard text field: white, hairline border, control radius, orange focus ring. */
export const fieldStyles = css`
	width: 100%;
	height: 44px;
	border: 1px solid ${border2};
	border-radius: ${radii.inner};
	background: ${white};
	padding: 0 14px;
	font-family: ${fontBody};
	font-size: ${fontSizes.base};
	color: ${black};
	transition:
		border-color ${motion.base},
		box-shadow ${motion.base};

	&::placeholder {
		color: ${black}80;
	}

	&:focus {
		outline: none;
		border-color: ${orange};
		box-shadow: ${shadows.focus};
	}
`

/** A dashed "add one more" slot: quiet until hovered, when it takes the accent. */
export const dashedSlot = css`
	${flatButton}
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.375rem;
	width: 100%;
	padding: 0.625rem;
	border: 1px dashed ${border2};
	border-radius: ${radii.small};
	color: ${textMuted};
	font-size: ${fontSizes.caption};
	font-weight: 500;
	transition:
		border-color ${motion.fast},
		color ${motion.fast},
		background-color ${motion.fast};

	&:hover:not(:disabled) {
		border-color: ${orange};
		color: ${orange};
		background: ${orange}14;
	}

	&:disabled {
		opacity: 0.4;
	}
`
